import { WifiOff } from 'lucide-react';
import { useOnlineStatus } from '@/hooks/use-online-status';
import { useSyncStatus } from '@/hooks/use-sync-status';
import { cn } from '@/lib/utils';

/**
 * Persistent offline banner displayed at the top of the content area
 * when the device loses network connectivity.
 *
 * Shows the number of pending sync items so the engineer knows
 * their work is saved locally and will sync when back online.
 *
 * Animates in/out smoothly on connectivity change.
 */
export function OfflineBanner() {
  const { isOnline } = useOnlineStatus();
  const { totalPending } = useSyncStatus();

  if (isOnline) return null;

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-2 px-4 py-2',
        'bg-amber-500 text-white text-sm font-medium',
        'animate-in slide-in-from-top duration-300',
      )}
      role="alert"
      aria-live="polite"
    >
      <WifiOff className="h-4 w-4 shrink-0" />
      <span>
        You are offline.
        {totalPending > 0
          ? ` ${totalPending} item${totalPending !== 1 ? 's' : ''} saved locally — will sync when reconnected.`
          : ' Your work is being saved locally.'}
      </span>
    </div>
  );
}
